import type { FeatureAccessOptions, UserProductState } from '@blueshift-ui/auth';
import type { ComponentProps } from 'react';
import type { UserTraits } from '@blueshift-ui/analytics';

import { getUserTraits, useAnalytics } from '@blueshift-ui/analytics';
import { useEffect, useRef } from 'react';
import BlueshiftUserAccessBoundary from '@blueshift-ui/auth/dist/components/user-access-boundary';
import isEqual from 'lodash/isEqual';
import useAuthenticatedUser from '@blueshift-ui/auth/dist/hooks/use-authenticated-user';
import useFeatureAccess from '@blueshift-ui/auth/dist/hooks/use-feature-access';

type UserAccessBoundaryProps = Omit<
  ComponentProps<typeof BlueshiftUserAccessBoundary>,
  'applicationId' | 'roles'
>;

const DEBUG_MODE_CRITERIA: FeatureAccessOptions = {
  flag: 'engage-867-user-access-boundary-debug',
};

const VT4S_ACCESS_CRITERIA: FeatureAccessOptions = {
  flag: 'ENGAGE-849_vt4s-student-access',
};

function UserAccessBoundary({
  children,
  ...userAccessBoundaryProps
}: Readonly<UserAccessBoundaryProps>) {
  const { data: authenticatedUser } = useAuthenticatedUser();
  const analytics = useAnalytics();
  const cachedUserTraits = useRef<UserTraits | null>(null);

  const { hasAccess: isDebugModeEnabled, isLoading: isLoadingDebugModeAccess } = useFeatureAccess({
    criteria: DEBUG_MODE_CRITERIA,
  });

  const { hasAccess: hasVt4sAccess, isLoading: isLoadingVt4sAccess } = useFeatureAccess({
    criteria: VT4S_ACCESS_CRITERIA,
  });

  useEffect(() => {
    const userTraits = getUserTraits(authenticatedUser) ?? null;

    if (!userTraits?.user_id || isEqual(userTraits, cachedUserTraits.current)) {
      return;
    }

    cachedUserTraits.current = userTraits;

    analytics.identify(userTraits.user_id, userTraits);
  }, [analytics, authenticatedUser]);

  if (isLoadingDebugModeAccess || isLoadingVt4sAccess) {
    return null;
  }

  const allowedProductStates: UserProductState[] = [
    'one_on_one_membership',
    ...(hasVt4sAccess ? (['vt4s'] as const) : []),
  ];

  return (
    <BlueshiftUserAccessBoundary
      applicationId="client-account-ui/account-manager"
      debug={isDebugModeEnabled}
      productStates={allowedProductStates}
      roles={['client']}
      {...userAccessBoundaryProps}
    >
      {children}
    </BlueshiftUserAccessBoundary>
  );
}

export default UserAccessBoundary;
